import styles from './ClassNameEditor.module.css';
import React, { useState, useEffect, useRef } from 'react';

import { CourseNameHints, tidyCourseNameOnBlur, useTidiedFlash } from '../../../components/CourseNameField';
import type { SessionLike } from '../../../utils/courseName';

/** Inline editor that appears when you click the class name */
const ClassNameEditor: React.FC<{
  name: string;
  sessions?: SessionLike[];
  onSave: (newName: string) => void;
}> = ({ name, sessions, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [tidiedTo, setTidiedTo] = useTidiedFlash();
  const inputRef = useRef<HTMLInputElement>(null);

  // focus + select when we switch to edit mode
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Keep the draft in step when the name changes underneath us — a Retry on a failed import
  // renames the class while this editor is closed.
  useEffect(() => {
    if (!editing) setValue(name);
  }, [name, editing]);

  const finish = () => {
    // Tidy the unambiguous mistakes silently, then save whatever we end up with.
    const trimmed = value.trim();
    const tidied = tidyCourseNameOnBlur(trimmed, setValue) ?? trimmed;
    setTidiedTo(tidied === trimmed ? null : tidied);
    setEditing(false);
    if (tidied !== name) onSave(tidied);
  };

  const apply = (suggested: string) => {
    setTidiedTo(null);
    setValue(suggested);
    if (suggested !== name) onSave(suggested);
  };

  if (!editing)
    return (
      <span className={styles.classNameWrapper}>
        <span
          className={styles.classNameDisplay}
          onClick={() => setEditing(true)}
          title="Click to edit"
        >
          {name}
        </span>
        <CourseNameHints value={name} sessions={sessions} onApply={apply} tidiedTo={tidiedTo} />
      </span>
    );

  return (
    <span className={styles.classNameWrapper}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => { setTidiedTo(null); setValue(e.target.value); }}
        onBlur={finish}
        onKeyDown={e => e.key === 'Enter' ? finish() : e.key === 'Escape' && setEditing(false)}
        className={styles.classNameInput}
      />
      <CourseNameHints value={value} sessions={sessions} onApply={apply} tidiedTo={tidiedTo} />
    </span>
  );
};


export { ClassNameEditor }
